import React, { useState } from 'react';
import { ArrowLeft, User, Check, X, AlertCircle } from 'lucide-react';

const ChangeUsernameView = ({ currentUsername, onBack, onUpdate }) => {
  const [username, setUsername] = useState(currentUsername);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const validateUsername = (username) => {
    if (username.length < 3) {
      return 'Username must be at least 3 characters long';
    }
    if (username.length > 20) {
      return 'Username must be less than 20 characters';
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      return 'Username can only contain letters, numbers, and underscores';
    }
    return '';
  };

  const handleSave = async () => {
    const validationError = validateUsername(username);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (username === currentUsername) {
      onBack();
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check if username is available (simulated)
      if (username === 'takenusername') {
        setError('This username is already taken');
        return;
      }

      onUpdate(username);
      onBack();
    } catch (err) {
      setError('Failed to update username. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setUsername(currentUsername);
    setError('');
    onBack();
  };

  const handleUsernameChange = (value) => {
    setUsername(value);
    if (error) {
      setError('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <div className="flex items-center space-x-3 mb-4">
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Change Username</h2>
            <p className="text-gray-600 text-sm">Choose a new username for your account</p>
          </div>
        </div>
      </div>

      {/* Current Username */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex items-center space-x-3 mb-6">
          <User className="w-6 h-6 text-gray-500" />
          <div>
            <p className="text-sm text-gray-600">Current Username</p>
            <p className="text-lg font-semibold text-gray-900">@{currentUsername}</p>
          </div>
        </div>

        {/* New Username Input */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Username
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500">@</span>
              </div>
              <input
                type="text"
                value={username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                placeholder="Enter new username"
                className="block w-full pl-8 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading}
              />
            </div>
            
            {/* Character Count */}
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-gray-500">
                Username can only contain letters, numbers, and underscores
              </p>
              <p className={`text-xs ${
                username.length > 20 ? 'text-red-500' : 'text-gray-500'
              }`}>
                {username.length}/20
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Requirements */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 text-sm mb-2">Requirements:</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li className={`flex items-center space-x-2 ${
                username.length >= 3 ? 'text-green-600' : ''
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${
                  username.length >= 3 ? 'bg-green-500' : 'bg-gray-400'
                }`} />
                <span>At least 3 characters long</span>
              </li>
              <li className={`flex items-center space-x-2 ${
                username.length <= 20 ? 'text-green-600' : ''
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${
                  username.length <= 20 ? 'bg-green-500' : 'bg-gray-400'
                }`} />
                <span>Maximum 20 characters</span>
              </li>
              <li className={`flex items-center space-x-2 ${
                /^[a-zA-Z0-9_]+$/.test(username) ? 'text-green-600' : ''
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${
                  /^[a-zA-Z0-9_]+$/.test(username) ? 'bg-green-500' : 'bg-gray-400'
                }`} />
                <span>Only letters, numbers, and underscores</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <div className="flex space-x-3">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || username === currentUsername || !!validateUsername(username)}
            className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeUsernameView;